import { DateTime, Duration, DurationObjectUnits } from "luxon";

import { DATE_FORMAT, LUXON_FORMAT } from "@/types/enum/luxonEnum";

const convertFormat = (date: string | undefined, from: LUXON_FORMAT, to: LUXON_FORMAT): string => {
    let formattedDate: DateTime | undefined
    if (!date || !from || !to) return ''
    switch (from) {
        case "iso":
            formattedDate = DateTime.fromISO(date)
            if (!formattedDate || !formattedDate.isValid) return ''
            break;
        default:
            formattedDate = DateTime.fromFormat(date, from)
            if (!formattedDate || !formattedDate.isValid) return ''
            break;
    }

    switch (to) {
        case "iso":
            return formattedDate.toISO() ?? ''
        default:
            return formattedDate.toFormat(to)
    }
}

export const ensureUTC = (value: string | DateTime): string => {
    const date = ensureDateTime(value)
    if (!date.isValid) {
        if (typeof value === 'string') return value
        return value.toString()
    }

    return date.toUTC().toString()
}

export const ensureNoOffset = (value: string | DateTime): string => {
    const date = ensureDateTime(value)
    if (!date.isValid) {
        if (typeof value === 'string') return value
        return value.toString()
    }

    return date.toISO({}) ?? ''
}

const ensureDateTime = (value: DateTime | string): DateTime => {
    if (typeof value === 'string') return DateTime.fromISO(value)
    return value
}

export const sameDay = (a?: DateTime | string, b?: DateTime | string): boolean => {
    if (!a || !b) return false

    const aDateTime: DateTime = ensureDateTime(a);
    if (!aDateTime.isValid) return false

    const bDateTime: DateTime = ensureDateTime(b);
    if (!bDateTime.isValid) return false

    return aDateTime.hasSame(bDateTime, "day") &&
        aDateTime.hasSame(bDateTime, "month") &&
        aDateTime.hasSame(bDateTime, "year");
}

export const luxonNow = (format: LUXON_FORMAT = DATE_FORMAT.DE): string => {
    if (format === 'iso') return DateTime.now().toISO() ?? ''
    return DateTime.now().toFormat(format)
}

export const luxonFromNow = (diff: Duration, format: LUXON_FORMAT = DATE_FORMAT.DE): string => {
    if (format === 'iso') return DateTime.now().plus(diff).toISO() ?? ''
    return DateTime.now().plus(diff).toFormat(format)
}

export const sameMonth = (a?: DateTime | string, b?: DateTime | string): boolean => {
    if (!a || !b) return false

    const aDateTime: DateTime = ensureDateTime(a);
    if (!aDateTime.isValid) return false

    const bDateTime: DateTime = ensureDateTime(b);
    if (!bDateTime.isValid) return false

    return aDateTime.hasSame(bDateTime, "month") &&
        aDateTime.hasSame(bDateTime, "year");
}

// Show difference between two days
export const diffDates = (
    firstDate: DateTime,
    secondDate: DateTime = DateTime.now(),
    options: Array<keyof DurationObjectUnits> = ['days', 'hours', 'minutes']
): DurationObjectUnits => {
    return firstDate.diff(secondDate, options).toObject()
}

export const isExpired = (firstDate: DateTime, condition: {
    value: number,
    unit: keyof DurationObjectUnits
}): boolean => {
    const diff: DurationObjectUnits = diffDates(firstDate, undefined, [condition.unit])

    if (!diff) return false

    return (diff[condition.unit] ?? 0) < condition.value
}

export default convertFormat

